import React, { useState, useEffect } from "react";
import {
  X,
  AlertCircle,
  CheckCircle,
  FileText,
  Calendar,
  DollarSign,
  Bell,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PendingActionsAlert = ({ pendingActions, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Auto-hide after 15 seconds if user doesn't interact
    const timer = setTimeout(() => {
      handleClose();
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const handleActionClick = (url) => {
    navigate(url);
    handleClose();
  };

  const getIcon = (type) => {
    switch (type) {
      case "purchase_request":
        return <FileText className="h-5 w-5" />;
      case "leave_request":
        return <Calendar className="h-5 w-5" />;
      case "expense_claim":
        return <DollarSign className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getIconBg = (type) => {
    switch (type) {
      case "purchase_request":
        return "bg-blue-500";
      case "leave_request":
        return "bg-purple-500";
      case "expense_claim":
        return "bg-green-500";
      default:
        return "bg-orange-500";
    }
  };

  if (!pendingActions || pendingActions.total === 0) {
    return null;
  }

  return (
    <div
      className={`fixed top-20 right-6 z-50 w-[420px] transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      {/* Premium Card with Gradient Border */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Gradient Header */}
        <div className="relative bg-gradient-to-r from-orange-500 to-red-500 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Bell className="h-5 w-5 text-white animate-pulse" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {pendingActions.total} Pending Action{pendingActions.total > 1 ? "s" : ""}
                </h3>
                <p className="text-xs text-white/80">Requires your attention</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-3 max-h-[400px] overflow-y-auto">
          {pendingActions.details.map((action, index) => (
            <div
              key={index}
              onClick={() => handleActionClick(action.url)}
              className="group relative bg-gray-50 hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 rounded-xl p-4 cursor-pointer transition-all duration-200 border border-gray-100 hover:border-orange-200 hover:shadow-md"
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className={`flex-shrink-0 w-10 h-10 ${getIconBg(action.type)} rounded-lg flex items-center justify-center text-white shadow-sm`}>
                  {getIcon(action.type)}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                    {action.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center">
                    <span className="inline-block w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></span>
                    Click to review
                  </p>
                </div>
                
                {/* Badge */}
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-sm">
                    {action.count}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            I'll review later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingActionsAlert;
